import React, {CSSProperties, ReactNode, forwardRef, useEffect, useMemo, useState} from 'react';
import {useSortable} from '@dnd-kit/sortable';
// eslint-disable-next-line node/no-extraneous-import
import {CSS} from '@dnd-kit/utilities';
import {PageInterface} from '@taopix/taopix-core/dist/types/types';
import {useTranslation} from 'react-i18next';
import classNames from 'classnames';
import {Common, ViewMode} from '@taopix/taopix-core';
import {HelpTip, Horizontal, Vertical} from '@taopix/taopix-design-system';
import {useExperience} from '../Experience/ExperienceProvider';
import useViewMode from '../../Hooks/useViewMode';
import {useTaopix} from '../Context/TaopixContext';

export type SortableItemProps = {
  element?: 'li' | 'div' | 'p' | 'span' | 'section' | 'nav';
  children: ReactNode;
  page: PageInterface;
  styles?: React.CSSProperties;
  className?: string;
};

const SortablePage = forwardRef(
  ({element = 'li', children, page, styles = {}, className}: SortableItemProps, fwdRef) => {
    const {experience} = useExperience();
    const [viewMode] = useViewMode();
    const taopix = useTaopix();
    const [sortDisabled, setSortDisabled] = useState(
      !experience.EDITOR.workArea.gridView.canRearrangePages
    );
    const [sortableId, setSortableId] = useState<number | undefined>(page.id);
    const [showLockedTip, setShowLockedTip] = useState<boolean>(false);

    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
      id: sortableId as number,
      disabled: sortDisabled,
    });

    const {t} = useTranslation();

    // add no deps so this is called every render
    useEffect(() => {
      setSortableId(page.id);
      setSortDisabled(false);
    });

    // add no deps so this is called every render
    useEffect(() => {
      const api = taopix.core.get('instances').get(page.pagenumber);
      if (api) {
        if (ViewMode.Grid !== viewMode) {
          setSortDisabled(true);
          setSortableId(undefined);
        }

        if (
          !experience.EDITOR.workArea.gridView.canRearrangePages ||
          api.insideLeft() ||
          api.outsideRight() ||
          Common.isPageCover(page.pagenumber)
        ) {
          setSortDisabled(true);
          // setting the id to undefined will prevent disabled thumbnails from being shifted
          setSortableId(undefined);
        }
      }
    });

    const style: CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    if (isDragging && !sortDisabled) {
      style.zIndex = 1;
    }

    const itemClasses = classNames([
      'group',
      'flex',
      'flex-col',
      'snap-center',
      'touch-manipulation',
      'tapHighlightColour-[transparent]',
      !sortDisabled && ['draggable', isDragging && 'dragging'],
      className,
    ]);

    const Element = useMemo(() => {
      return element;
    }, [element]);

    return (
      <Element
        id={`page-${page.pagenumber}`}
        data-id={page.pagenumber}
        className={itemClasses}
        ref={setNodeRef}
        style={{...styles, ...style}}
        {...(!sortDisabled && attributes)}
        {...(!sortDisabled && listeners)}
        {...(sortDisabled && {
          draggable: experience.EDITOR.workArea.gridView.canRearrangePages,
          onDragStart: event => {
            event.preventDefault();
            // only show the tip if we're permitted to drag
            if (experience.EDITOR.workArea.gridView.canRearrangePages) {
              setShowLockedTip(true);
            }
          },
        })}
      >
        {children}
        {sortDisabled && (
          <HelpTip
            open={showLockedTip}
            anchorId={`page-${page.pagenumber}`}
            message={t('*:str_MessageThisPageCannotBeMoved')}
            hideAfter={2000}
            onClose={() => setShowLockedTip(false)}
            anchorOrigin={{horizontal: Horizontal.Centre, vertical: Vertical.Top}}
            transformOrigin={{horizontal: Horizontal.Centre, vertical: Vertical.Bottom}}
            arrow
            popDirection="up"
            edgeBehaviour="move"
            className={'max-w-[240rem]'}
          />
        )}
      </Element>
    );
  }
);

export default SortablePage;
